import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '~/components/Layout';
import { emailRegex } from '@src/components/FormFields/constants';
import { FormFields } from '~/components/FormFields';
import { useForm } from 'react-hook-form';
import { Form, Icon, Input, Dropdown } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import notify from '~/components/Notify';
import { UsersQuery } from '~/QueryHooks/UsersQuery';
import { useAuth } from '~/routes/components/AuthProvider';
import FileUpload from '~/components/FileUpload'
import { RouteTypes } from '~/routes/constants';
import { StyledImageWrapper, StyledImageContainer } from './style';
import UsersService from '~/api/services/users';
import { handleErrors } from '~/utils/helpers';

const defaultImage = '/assets/default.png';

const CreateEditUser: React.FC = () => {
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth();
  const params = useParams()

  const { handleSubmit, control, setValue, watch, reset } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    fetchFile()
  }, [])

  async function fetchFile() {
    if (params.id) {
      try {
        const response = await UsersService.getUserFile(params.id)
        setValue('photo', response.data)
      } catch (error) {
        handleErrors(error)
      }
    }
  }

  function onSuccess() {
    if (params.id) {
      notify(t('User successfully updated'), 'success');
    } else {
      notify(t('User successfully created'), 'success');
    }
    refetchEditUserData();
  }

  async function onGetUserDataSuccess(data) {
    if (params.id) {
      const response = await UsersService.getUserFile(params.id)
      setValue('photo', response.data)
      reset({ ...data, photo: response.data });
    }
  }

  const {
    data: usersOptions,
    isLoading: isBusyUsersOptions
  } = UsersQuery.getUserOptionsData();

  const {
    isLoading: isBusyUserData,
    isFetching: isFetching,
    data: userData,
    refetch: refetchEditUserData
  } = UsersQuery.getEditUser(onGetUserDataSuccess, params.id);

  const photoFieldUrl = watch('photo')
  const watchPhoneCodeField = watch('phonePrefix')
  const wathcMobileCodeField = watch('mobilePrefix')
  const mobileNumber = watch('mobileNumber')
  const phoneNumber = watch('phoneNumber')

  const { isLoading: isUserStoreLoading, mutateAsync: storeUser } =
    UsersQuery.storeUser(onSuccess);

  const { isLoading: isUserEditLoading, mutateAsync: editUser } =
    UsersQuery.editUser(onSuccess);

  const onSubmit = (data) => {
    if (params.farmerId) {
      data.farmer_id = params.farmerId;
    }
    try {
      if (!params.id) {
        storeUser(data);
      } else {
        editUser(data);
      }
    } catch (err) {
      console.log('onSubmit error', err);
    }
  };

  function renderUserForm() {
    return <>
      <Form.Field>
        {photoFieldUrl && <StyledImageWrapper>
          <img src={photoFieldUrl ? photoFieldUrl : defaultImage}></img>
          <StyledImageContainer />
          {photoFieldUrl && <Icon
            circular
            onClick={(e) => { e.preventDefault(); setValue('photo', null) }}
            name='trash'
            className='trashCan'
          />
          }
        </StyledImageWrapper>
        }
        {!photoFieldUrl && <FileUpload
          encodeFiles
          maxWidthDropZone={'100%'}
          onChange={(value) => setValue('photo', value)}
          accept={{
            'image/bmp': ['.bmp'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png']
          }}
        />
        }
      </Form.Field>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={t('First name')}
          rules={{
            required: {
              value: true,
              message: t('Please enter first name')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'firstName'}
          placeholder={t('First name')}
        />
        <FormFields.Input
          control={control}
          label={t('Last name')}
          rules={{
            required: {
              value: true,
              message: t('Please enter last name')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          style={{ marginBottom: 10 }}
          name={'lastName'}
          placeholder={t('Last name')}
        />
      </Form.Group>
      {!(params.id && userData) && <FormFields.Input
        name='email'
        type='text'
        label={t('Email')}
        rules={{
          required: {
            value: true,
            message: t('Please enter email')
          },
          pattern: {
            value: emailRegex,
            message: t('Please enter valid email')
          }
        }}
        style={{ marginBottom: 10 }}
        autoComplete='off'
        placeholder={t('Email')}
        control={control}
      />
      }
      <FormFields.Select
        options={languages?.map(language => ({ value: language?.id, text: language?.label }))}
        selection
        style={{ marginBottom: 10 }}
        selectOnBlur={false}
        placeholder={t('Select Language')}
        control={control}
        label={<label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <Icon name={'world'} style={{ fontSize: '1.2em' }}/>
          <div>{t('Language')}</div>
        </label>}
        name={'languageId'}
      />
      <Form.Group widths={'equal'}>
        <Form.Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'text telephone'} style={{ fontSize: '1.2em' }}/>
            <div>{t('Phone number')}</div>
          </label>
          <Input
            value={phoneNumber || ''}
            label={<Dropdown
              options={usersOptions?.countries.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
              selectOnBlur={false}
              value={parseInt(watchPhoneCodeField) || ''}
              scrolling
              basic
              placeholder={t('Code')}
              clearable
              onChange={(e, data) => setValue('phonePrefix', data.value)}
              name={'phonePrefix'}
            />}
            onChange={(e, data) => {
              setValue('phoneNumber', data.value)
            }}
            labelPosition='left'
            style={{ marginBottom: 10 }}
            placeholder={watchPhoneCodeField ? t('Phone number') : t('Please select phone code')}
          />
        </Form.Field>
        <Form.Field style={{ paddingLeft: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Icon name={'mobile alternate'} style={{ fontSize: '1.2em' }}/>
            <div>{t('Mobile number')}</div>
          </label>
          <Input
            value={mobileNumber || ''}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={usersOptions?.countries.map(countryCode => ({ value: countryCode.id, text: countryCode.phoneCode, flag: countryCode.iso }))}
              selectOnBlur={false}
              value={parseInt(wathcMobileCodeField) || ''}
              onChange={(e, data) => setValue('mobilePrefix', data.value)}
              placeholder={t('Code')}
              name={'mobilePrefix'}
            />}
            onChange={(e, data) => setValue('mobileNumber', data.value)}
            labelPosition='left'
            style={{ marginBottom: 10 }}
            type={'number'}
            placeholder={wathcMobileCodeField ? t('Mobile number') : t('Please select mobile code')}
            name={'mobileNumber'}
          />
        </Form.Field>
      </Form.Group>
    </>
  }

  const email = userData?.email
  const firstName = userData?.firstName
  const lastName = userData?.lastName

  return <Layout
    segmented
    title={!isFetching && params.id && userData ? `${firstName} ${lastName} (${email})` : t('Create user')}
    // close={params.farmerId? RouteTypes.FARMER_USERS : RouteTypes.USERS}
    close={RouteTypes.DAIRY_FACTORY_FARMER_USERS}
    isBusy={isBusyUsersOptions || isBusyUserData || isUserStoreLoading || isUserEditLoading}
    action={params.id && userData ? t('Update') : t('Create')}
    onSubmit={handleSubmit(onSubmit)}
  >
    {!isFetching && renderUserForm()}

  </Layout>
}
export default CreateEditUser
